import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Send, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear email error when user types
    if (e.target.name === 'email' && emailError) {
      setEmailError('');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setEmailError('');

    // Validate email format
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.response?.status === 422) {
        setEmailError('Please enter a valid email address');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FDFBF7] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6">
              Let's Create Something Exceptional
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl mx-auto">
              Ready to discuss your workspace or gaming setup project? We're here to help you create premium
              environments that users will love.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="pb-24 sm:pb-32" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-1"
            >
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight text-[#0A1F38] mb-6">
                Get in Touch
              </h2>
              <div className="space-y-6">
                <div>
                  <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A1F38] mb-2">Email</h4>
                  <a
                    href="mailto:pontus.eriksson@pepconsulting.net"
                    className="flex items-center space-x-2 text-[#475569] hover:text-[#C5A05B] transition-colors duration-300"
                    data-testid="contact-email-link"
                  >
                    <Mail size={20} />
                    <span className="font-['Manrope'] text-base">pontus.eriksson@pepconsulting.net</span>
                  </a>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A1F38] mb-2">Company</h4>
                  <p className="font-['Manrope'] text-base text-[#475569]">PEP Consulting AB</p>
                </div>
                <div className="pt-6 border-t border-[#E2E8F0]">
                  <p className="font-['Manrope'] text-sm text-[#475569] leading-relaxed">
                    We typically respond within 24 hours. For urgent inquiries, please mention it in your
                    message.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label
                    htmlFor="name"
                    className="block font-['Manrope'] text-sm font-medium text-[#0A1F38] mb-2"
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] text-[#0A1F38] font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-[#C5A05B] focus:border-transparent transition-all duration-300"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block font-['Manrope'] text-sm font-medium text-[#0A1F38] mb-2"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 rounded-xl bg-white border text-[#0A1F38] font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-[#C5A05B] focus:border-transparent transition-all duration-300 ${
                      emailError ? 'border-red-500' : 'border-[#E2E8F0]'
                    }`}
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                  {emailError && (
                    <p className="mt-2 text-sm text-red-600 font-['Manrope']" data-testid="email-error-message">
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block font-['Manrope'] text-sm font-medium text-[#0A1F38] mb-2"
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] text-[#0A1F38] font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-[#C5A05B] focus:border-transparent transition-all duration-300"
                    placeholder="Your company name"
                    data-testid="contact-company-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-['Manrope'] text-sm font-medium text-[#0A1F38] mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E2E8F0] text-[#0A1F38] font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-[#C5A05B] focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Tell us about your project or inquiry..."
                    data-testid="contact-message-input"
                  />
                </div>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-xl"
                    data-testid="contact-success-message"
                  >
                    <CheckCircle2 className="text-green-600" size={20} />
                    <p className="font-['Manrope'] text-sm text-green-800">
                      Thank you! We've received your message and will respond soon.
                    </p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-xl"
                    data-testid="contact-error-message"
                  >
                    <p className="font-['Manrope'] text-sm text-red-800">
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-base transition-all duration-300 hover:bg-[#0A1F38] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] disabled:opacity-50 disabled:cursor-not-allowed group"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="contact-info">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              What to Expect
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  step: '01',
                  title: 'Discovery Call',
                  description: 'We discuss your needs, environment, and goals',
                },
                {
                  step: '02',
                  title: 'Tailored Proposal',
                  description: 'Receive a customized solution concept and quote',
                },
                {
                  step: '03',
                  title: 'Implementation',
                  description: 'Professional deployment and ongoing support',
                },
              ].map((item, index) => (
                <div key={index} className="text-left" data-testid={`expect-step-${index}`}>
                  <span className="font-['Outfit'] text-5xl font-bold text-[#C5A05B]/20">{item.step}</span>
                  <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mt-3 mb-2">
                    {item.title}
                  </h4>
                  <p className="font-['Manrope'] text-base text-[#475569]">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
